import type { BaseViewerOptions } from "bpmn-js/lib/BaseViewer";
import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import Viewer from "bpmn-js/lib/Viewer";
import ModelingModule from "bpmn-js/lib/features/modeling";
import type { ImportBPMNDiagramError as OriginalImportDiagramError } from "bpmn-js/lib/import/Importer";
import type { Element as BpmnElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject, is } from "bpmn-js/lib/util/ModelUtil";
// @ts-expect-error: Package doesn't have types.
import GridModule from "diagram-js-grid";
import type { CanvasViewbox } from "diagram-js/lib/core/Canvas";
import type DirectEditing from "diagram-js/lib/features/direct-editing/DirectEditing";
import { isPaste } from "diagram-js/lib/features/keyboard/KeyboardUtil";
import type { ElementLike } from "diagram-js/lib/model/Types";
import { computed, markRaw, readonly, ref, shallowReadonly, watch } from "vue";

import { useProcessManager } from "@/composables/process-manager";
import { useLanesManager } from "@/stores/lanes-store";
import { useProcessElementsManager } from "@/stores/process-elements-store.js";
import { useSyncManager } from "@/stores/sync-store.js";

import LaneLabelRenderer from "./custom-modules/lane-label-renderer";
import {
    Modeler as BaseModeler,
    HIGH_PRIORITY,
    createBpmnModeler,
    createReviver,
    elementIsCallActivity,
    elementIsCollapsedPool,
    elementIsEvent,
    elementIsGateway,
    elementIsLane,
    elementIsMessageFlow,
    elementIsSequenceFlow,
    elementIsSubProcess,
    elementIsTask,
    getMainParticipant,
    isProcessParticipant,
} from "./helpers";
import StekzModdle from "./stekz-moddle.json";

/**
 * Overwrite wrong types for Canvas.zoom.
 */
declare module "diagram-js/lib/core/Canvas" {
    export default interface Canvas {
        zoom(newScale?: number | "fit-viewport", center?: Point | true): number;
    }
}

/**
 * Warnings are wrongly typed as `string`, but actually look like this.
 * Added the `string` as a type as well since we MAY be wrong in some cases?
 */
export type ImportWarning = {
    message: string;
    [key: string]: unknown;
} & string;
export type ImportBPMNDiagramError = OriginalImportDiagramError & {
    warnings: ImportWarning[];
};

let _isUpdatingLabel = false;

export function useBpmnManager<BpmnJs extends BaseModeler>() {
    const bpmnJs = ref<BpmnJs>();
    const isChanged = ref(false);
    const bpmnJsElement = ref<HTMLElement>();
    const selectedBpmnElement = ref<BpmnElement>();
    const hoveredBpmnElement = ref<BpmnElement>();
    const xml = ref("");
    const scrollingEnabled = ref(false);
    const navigationEnabled = ref(false);
    const xOffsetWhenCentered = ref(0);
    const yOffsetWhenCentered = ref(0);
    const { lanes } = useLanesManager();
    const { processElements } = useProcessElementsManager();
    const { process } = useProcessManager();
    const { isActivelyEditing } = useSyncManager();

    const eventBus = computed(() => bpmnJs.value?.get("eventBus"));

    const detachBeforeInit = () => {
        if (bpmnJsElement.value) {
            bpmnJsElement.value.removeEventListener("wheel", wheelZoomListener);
            bpmnJsElement.value.removeEventListener(
                "wheel",
                wheelScrollListener
            );
        }

        if (bpmnJs.value) {
            bpmnJs.value.clear();
            bpmnJs.value.detach();
            try {
                bpmnJs.value.get("propertiesPanel")?.detach();
            } catch {
                // Has no propertiesPanel
            }
        }
        xml.value = "";

        selectedBpmnElement.value = undefined;
        hoveredBpmnElement.value = undefined;
    };

    /**
     * Manually trigger a mousedown event on the root element to complete active actions.
     * This is useful to complete ongoing action, for example when
     * the user is editing a label.
     */
    const completeActiveActions = async () => {
        const rootElement = getRootElement();

        return new Promise<void>((resolve, reject) => {
            const callback = ({ element }: { element: BpmnElement }) => {
                if (!rootElement) reject();

                if (element.id == rootElement?.id) {
                    resolve();
                }
                // Remove the listener
                bpmnJs.value?.off("element.mousedown", callback);
            };

            bpmnJs.value?.on<{ element: BpmnElement } & Event>(
                "element.mousedown",
                callback
            );

            eventBus.value?.fire("element.mousedown", {
                element: rootElement,
                originalEvent: { target: rootElement },
            });
        });
    };

    /**
     * Initialize as a BPMN Modeler.
     */
    const initModeler = (
        element: HTMLElement,
        propertiesPanelElement?: HTMLElement,
        {
            hideLaneNames = false,
        }: {
            hideLaneNames?: boolean;
        } = {}
    ) => {
        isChanged.value = false;
        detachBeforeInit();

        bpmnJs.value = markRaw(
            createBpmnModeler(propertiesPanelElement, {
                hideLaneNames,
            }) as BpmnJs
        );
        scrollingEnabled.value = true;
        navigationEnabled.value = true;
        attachTo(element);
        if (propertiesPanelElement) {
            attachPropertiesPanel(propertiesPanelElement);
        }
        addBpmnEventListeners();
    };

    /**
     * Initialize as a BPMN Viewer.
     */
    const initViewer = (
        element?: HTMLElement | undefined,
        {
            isNavigationEnabled = true,
            isScrollingEnabled = true,
            hideLaneNames = false,
        }: {
            isNavigationEnabled?: boolean;
            isScrollingEnabled?: boolean;
            hideLaneNames?: boolean;
        } = {}
    ) => {
        detachBeforeInit();
        const _Viewer = isNavigationEnabled ? NavigatedViewer : Viewer;
        const additionalModules: BaseViewerOptions["additionalModules"] = [
            ModelingModule,
        ];
        if (hideLaneNames) {
            additionalModules.push(LaneLabelRenderer);
        }
        if (isNavigationEnabled) {
            additionalModules.push(GridModule);
        }

        bpmnJs.value = markRaw(
            new _Viewer({
                additionalModules: additionalModules,
                moddleExtensions: {
                    stekz: StekzModdle,
                },
                zoomScroll: {
                    scale: 0.3,
                    enabled: false,
                },
            }) as BpmnJs
        );

        scrollingEnabled.value = isScrollingEnabled;
        navigationEnabled.value = isNavigationEnabled;
        if (element) {
            attachTo(element);
        }
        addBpmnEventListeners();
    };

    const getRootElement = () => {
        const canvas = bpmnJs.value?.get("canvas");
        return canvas?.getRootElement();
    };

    /**
     * Set the root element.
     */
    const setRootElement = (id: string) => {
        const canvas = bpmnJs.value?.get("canvas");
        const rootElement = canvas?.findRoot(id);
        if (rootElement) {
            canvas?.setRootElement(rootElement);
        }
    };

    /**
     * Injects styles into the BPMN container.
     */
    const injectStyles = (newStyles: string) => {
        if (!bpmnJsElement.value) {
            return;
        }
        const styleNode =
            bpmnJsElement.value.querySelector("style") ||
            document.createElement("style");
        styleNode.innerHTML = newStyles;
        if (bpmnJsElement.value.firstChild) {
            bpmnJsElement.value.insertBefore(
                styleNode,
                bpmnJsElement.value.firstChild
            );
        } else {
            bpmnJsElement.value.appendChild(styleNode);
        }
    };

    /**
     * Listens to "wheel" event to zoom in and out.
     */
    const wheelZoomListener = (event: WheelEvent) => {
        if (!bpmnJs.value || !navigationEnabled.value) {
            return;
        }

        if (!event.metaKey && !event.ctrlKey) {
            // Only act if CMD or CTRL is clicked
            return;
        }
        event.preventDefault();

        const elementRect = bpmnJs.value
            .get("canvas")
            .getContainer()
            .getBoundingClientRect();
        const offset = {
            x: event.clientX - elementRect.left,
            y: event.clientY - elementRect.top,
        };

        const factor = -0.75 * (event.deltaMode === 0 ? 0.02 : 0.32);
        const delta =
            Math.sqrt(Math.pow(event.deltaY, 2) + Math.pow(event.deltaX, 2)) *
            Math.sign(event.deltaY) *
            factor;

        bpmnJs.value.get("zoomScroll")!.zoom(delta, offset);
    };

    /**
     * Listens to "wheel" event to scroll
     */
    const wheelScrollListener = (event: WheelEvent) => {
        if (
            !bpmnJs.value ||
            !scrollingEnabled.value ||
            !navigationEnabled.value
        ) {
            return;
        }

        if (event.metaKey || event.ctrlKey) {
            // Only act if CMD or CTRL is NOT clicked
            return;
        }

        // If the mouse is on top of a popup, don't intercept.
        if (
            event.target instanceof HTMLElement &&
            event.target.closest("[class*='popup']")
        ) {
            return;
        }

        event.preventDefault();

        const factor = -0.75 * (event.deltaMode === 0 ? 1.0 : 16.0);
        const delta = { dx: 0, dy: 0 };

        if (event.shiftKey) {
            // Force horizontal scrolling
            delta.dx = factor * event.deltaY;
        } else {
            delta.dx = factor * event.deltaX;
            delta.dy = factor * event.deltaY;
        }
        bpmnJs.value.get("zoomScroll")?.scroll(delta);
    };

    const stepZoom = (stepSize: number) => {
        bpmnJs.value?.get("zoomScroll")?.stepZoom(stepSize);
    };

    const resetZoom = () => {
        const canvas = bpmnJs.value?.get("canvas");
        // Avoid resetting the zoom if the container is gone. This prevents
        // errors being thrown when resetZoom is being called right before
        // the component is unmounted.
        if (!canvas?.getContainer()) {
            return;
        }

        canvas.zoom("fit-viewport", true);

        const viewbox = canvas.viewbox();
        xOffsetWhenCentered.value = viewbox.x;
        yOffsetWhenCentered.value = viewbox.y;
    };

    const zoomToElementId = (elementId: string, scale = 1.0) => {
        const registry = bpmnJs.value?.get("elementRegistry");
        const element = registry?.find((element: ElementLike) => {
            return element.businessObject.id === elementId;
        }) as BpmnElement;
        if (!element) {
            return;
        }

        return zoomToElement(element, scale);
    };

    const zoomToElement = (element: BpmnElement, scale = 1.0) => {
        const canvas = bpmnJs.value?.get("canvas");
        if (!canvas) {
            console.log("Canvas not available to zoom.");
            return;
        }

        const bounds = element.di?.bounds;
        if (!bounds) {
            console.log("Element has no bounds.");
            return;
        }

        // First set the absolute scale
        canvas.zoom(scale);

        // Then center the view on the element
        const containerWidth = canvas.getContainer().clientWidth / scale;
        const containerHeight = canvas.getContainer().clientHeight / scale;

        // Calculate the center position of the element
        const elementCenterX = bounds.x + bounds.width / 2;
        const elementCenterY = bounds.y + bounds.height / 2;

        // Position the viewbox so the element is centered
        canvas.viewbox({
            x: elementCenterX - containerWidth / 2,
            y: elementCenterY - containerHeight / 2,
            width: containerWidth,
            height: containerHeight,
        });

        return scale;
    };

    /**
     * Attach BpmnJs instance to DOM.
     */
    const attachTo = (element: HTMLElement) => {
        bpmnJs.value?.attachTo(element);

        bpmnJsElement.value = element;
        // Zoom and scroll with scrollwheel
        bpmnJsElement.value.addEventListener("wheel", wheelZoomListener, {
            passive: false,
        });
        bpmnJsElement.value.addEventListener("wheel", wheelScrollListener, {
            passive: false,
        });
    };

    /**
     * Attach the propertiesPanel.
     */
    const attachPropertiesPanel = (element: HTMLElement) => {
        const propertiesPanel = bpmnJs.value?.get("propertiesPanel");
        propertiesPanel?.attachTo(element);
    };

    /**
     * Load the BPMN/XML into the canvas
     */
    const loadBpmn = async (
        xml_: string
    ): Promise<{ warnings?: ImportWarning[] }> => {
        if (xml_ === xml.value || !bpmnJs.value) {
            return {};
        }

        if (!xml_) {
            bpmnJs.value.clear();
            xml.value = xml_;
            return {};
        }

        try {
            const { warnings } = await bpmnJs.value.importXML(xml_);
            xml.value = xml_;
            // E.g. 'unknown attribute'.
            if (warnings.length) {
                console.warn(warnings);
                return { warnings } as unknown as { warnings: ImportWarning[] };
            }
        } catch (e) {
            detachBeforeInit();
            throw e as ImportBPMNDiagramError;
        }
        /*
         * Move to the end of the execution to prevent rendering issues on
         * some browsers.
         */
        setTimeout(() => {
            resetZoom();
            updateProcessElementProperties();
            updateLaneProperties();
        });

        return {};
    };

    const getSvg = async () => {
        if (!bpmnJs.value) {
            return;
        }
        const { svg } = await bpmnJs.value.saveSVG();
        return svg;
    };

    const resize = () => {
        const canvas = bpmnJs.value?.get("canvas");
        canvas?.resized();
    };

    const getViewbox = (): CanvasViewbox | undefined => {
        return bpmnJs.value?.get("canvas")?.viewbox();
    };

    const setViewbox = (viewBox: CanvasViewbox) => {
        bpmnJs.value?.get("canvas")?.viewbox(viewBox);
    };

    /**
     * Add event listeners to the bpmn-js instance.
     */
    const addBpmnEventListeners = () => {
        if (!bpmnJs.value || !bpmnJsElement.value) {
            return;
        }

        bpmnJs.value.on("directEditing.activate", () => {
            isActivelyEditing.value = true;
        });
        bpmnJs.value.on(
            ["directEditing.complete", "directEditing.cancel"],
            () => {
                isActivelyEditing.value = false;
            }
        );

        // Set the selectedBpmnElement.
        bpmnJs.value!.on<{ element: BpmnElement }>(
            "element.click",
            ({ element }) => {
                if (element === selectedBpmnElement.value) {
                    selectedBpmnElement.value = undefined;
                } else {
                    selectedBpmnElement.value = markRaw(element);
                }
            }
        );

        // Set the hoveredBpmnElement.
        bpmnJs.value.on<{ element: BpmnElement }>(
            "element.hover",
            ({ element }) => {
                hoveredBpmnElement.value = markRaw(element);
            }
        );
        bpmnJs.value.on<{ element: BpmnElement }>("element.out", () => {
            hoveredBpmnElement.value = undefined;
        });

        // Update the XML.
        bpmnJs.value.on("commandStack.changed", async () => {
            isChanged.value = true;
            const result = await bpmnJs.value?.saveXML({ format: true });
            xml.value = result?.xml || "";
        });

        bpmnJs.value.on<Event & { element: ElementLike }>(
            "element.dblclick",
            HIGH_PRIORITY,
            function ({ element }) {
                if (
                    is(element, "bpmn:Lane") ||
                    (is(element, "bpmn:Participant") &&
                        isProcessParticipant(element, process.value!.identity))
                ) {
                    // Cancel edit for lane and main participant labels
                    return false;
                }
            }
        );

        // Copy
        bpmnJs.value.on<{ tree: unknown }>(
            "copyPaste.elementsCopied",
            ({ tree }) => {
                localStorage.setItem("bpmnClipboard", JSON.stringify(tree));
            }
        );

        const moddle = bpmnJs.value.get("moddle");

        // Paste
        try {
            const clipboard = bpmnJs.value.get("clipboard");
            if (!clipboard) return;
            bpmnJs.value!.get("keyboard")?.addListener(2000, (event) => {
                const { keyEvent } = event;

                if (!isPaste(keyEvent)) {
                    return;
                }

                const serializedCopy = localStorage.getItem("bpmnClipboard");

                if (!serializedCopy) {
                    return;
                }

                const parsedCopy = JSON.parse(
                    serializedCopy,
                    createReviver(moddle)
                );

                clipboard.set(parsedCopy);
            });

            // Disable TokenSimulator keybinding (T key)
            // Setting priority to Number.MAX_SAFE_INTEGER to ensure it runs before token simulator
            bpmnJs
                .value!.get("keyboard")
                ?.addListener(Number.MAX_SAFE_INTEGER, (event) => {
                    const { keyEvent } = event;

                    if (keyEvent.key.toLowerCase() === "t") {
                        // Return false to prevent the event from being handled by the token simulator
                        return false;
                    }
                });
        } catch {
            // Clipboard does not exist.
        }

        bpmnJs.value.on<{
            context: {
                element: BpmnElement;
                properties: Record<string, string>;
            };
        }>(
            "commandStack.element.updateProperties.postExecute",
            ({ context }) => {
                if (_isUpdatingLabel) {
                    return;
                }
                const { element, properties } = context;

                if (
                    !properties.name ||
                    (!elementIsCollapsedPool(element) &&
                        !elementIsCallActivity(element))
                ) {
                    return;
                }

                const bo = getBusinessObject(element);

                // If we are updating the name, but not setting a new ID, and the name
                // no longer matches the path of the linked element, we wipe the link.
                const isSettingId =
                    properties.calledElementId || properties.processId;
                if (!isSettingId && bo.calledElementPath !== properties.name) {
                    const modeling = bpmnJs.value!.get("modeling");
                    _isUpdatingLabel = true;
                    modeling.updateProperties(element, {
                        calledElementId: undefined,
                        calledElementType: undefined,
                        calledElementPath: undefined,
                        processId: undefined,
                    });
                    _isUpdatingLabel = false;
                }
            }
        );
    };

    /**
     * Get all subprocesses from the BPMN.
     */
    const getSubProcesses = (): BpmnElement[] => {
        const registry = bpmnJs.value?.get("elementRegistry");
        return registry?.filter(elementIsSubProcess) as BpmnElement[];
    };

    /**
     * Get all subprocesses from the BPMN.
     */
    const getLanes = async (): Promise<BpmnElement[]> => {
        const registry = bpmnJs.value?.get("elementRegistry");
        return registry?.filter(elementIsLane) as BpmnElement[];
    };

    /**
     * Get the subprocess for a given activity id.
     * This is NOT the element.id for a subprocess, but the element.businessObject.id.
     */
    const getSubProcessById = (businessObjectId: string) => {
        const registry = bpmnJs.value?.get("elementRegistry");
        return registry?.find((element: ElementLike) => {
            return (
                elementIsSubProcess(element as BpmnElement) &&
                element.businessObject.id === businessObjectId
            );
        }) as BpmnElement;
    };

    /**
     * Open a subprocess inside the editor.
     */
    const selectSubProcess = (element: BpmnElement) => {
        if (!elementIsSubProcess(element)) {
            element = getSubProcessById(element.businessObject.id);
        }
        const canvas = bpmnJs.value?.get("canvas");
        canvas?.setRootElement(element);
    };

    /**
     * Destroy the bpmn-js component.
     */
    const destroy = () => {
        bpmnJs.value?.destroy();
    };

    const updateProcessElementProperties = () => {
        if (!bpmnJs?.value || !processElements?.value) return;

        const modeling = bpmnJs.value.get("modeling");
        const elementRegistry = bpmnJs.value.get("elementRegistry");
        let somethingHasChanged = false;

        elementRegistry
            .filter(
                (element: BpmnElement) =>
                    elementIsTask(element) ||
                    elementIsEvent(element) ||
                    elementIsGateway(element) ||
                    elementIsMessageFlow(element) ||
                    elementIsSequenceFlow(element)
            )
            .forEach((element: BpmnElement) => {
                const businessObject = getBusinessObject(element);
                const processElement = processElements?.value?.find(
                    (t) => t.bpmn_id === businessObject.id
                );
                if (!processElement) return;

                if (processElement.identity === businessObject.processElementId)
                    return;

                somethingHasChanged = true;
                modeling.updateProperties(element as BpmnElement, {
                    processElementId: processElement.identity,
                });
            });
        if (somethingHasChanged) {
            // Trigger a change for bpmnJs as well.
            eventBus.value?.fire("commandStack.changed");
        }
    };

    const updateLaneProperties = () => {
        if (!bpmnJs.value || !lanes.value) return;

        const modeling = bpmnJs.value.get("modeling");
        const elementRegistry = bpmnJs.value.get("elementRegistry");
        let somethingHasChanged = false;

        elementRegistry
            .filter((element) => is(element, "bpmn:Lane"))
            .forEach((laneElement) => {
                const businessObject = getBusinessObject(laneElement);
                const lane = lanes?.value?.find(
                    (l) => l.bpmn_id === businessObject.id
                );
                if (!lane) return;

                if (
                    lane.name === businessObject.name &&
                    lane.role_id === businessObject.roleId
                )
                    return;

                somethingHasChanged = true;
                modeling.updateProperties(laneElement as BpmnElement, {
                    name: lane.name,
                    roleId: lane.role_id,
                });
                modeling.updateLabel(laneElement as BpmnElement, lane.name);
            });
        if (somethingHasChanged) {
            // Trigger a change for bpmnJs as well.
            eventBus.value?.fire("commandStack.changed");
        }
    };

    watch(processElements, async () => {
        updateProcessElementProperties();
    });

    watch(lanes, async () => {
        updateLaneProperties();
    });

    const updateElementProperties = (
        id: string,
        newProperties: Record<string, unknown>
    ) => {
        const element = bpmnJs.value
            ?.get("elementRegistry")
            ?.find((el) => getBusinessObject(el).id === id);
        if (!element) {
            throw Error("Cannot find provided element by ID.");
        }
        const modeling = bpmnJs.value?.get("modeling");
        modeling?.updateProperties(element as BpmnElement, newProperties);

        const newName = newProperties.name as string | undefined;

        if (newName) {
            _isUpdatingLabel = true;
            modeling?.updateLabel(element as BpmnElement, newName);
            _isUpdatingLabel = false;
        }
        eventBus.value?.fire("commandStack.changed");
    };

    watch([bpmnJs, xml, process], async ([newBpmnJs, newXml, newProcess]) => {
        if (!newBpmnJs || !newXml || !newProcess) return;

        const participant = await getMainParticipant(
            newBpmnJs,
            newProcess.identity
        );
        if (!participant) return;

        const businessObject = getBusinessObject(participant);
        if (
            businessObject.name === newProcess.name &&
            businessObject.processId === newProcess.identity
        ) {
            return;
        }

        // Update the name and set as main explicitly
        const modeling = newBpmnJs.get("modeling");
        modeling.updateProperties(participant as BpmnElement, {
            name: newProcess.name,
            processId: newProcess.identity,
        });
        modeling.updateLabel(participant as BpmnElement, newProcess.name);

        // Trigger a change for bpmnJs as well.
        eventBus.value?.fire("commandStack.changed");
    });

    const triggerDirectEditing = (element: BpmnElement) => {
        const directEditing = bpmnJs.value?.get<DirectEditing>("directEditing");
        directEditing?.activate(element);
    };

    return {
        bpmnJs: shallowReadonly(bpmnJs),
        xml: readonly(xml),
        get initialized() {
            return computed(() => !!bpmnJs.value);
        },
        scrollingEnabled,
        resize,
        stepZoom,
        resetZoom,
        getSvg,
        getViewbox,
        setViewbox,
        initModeler,
        initViewer,
        injectStyles,
        attachTo,
        attachPropertiesPanel,
        loadBpmn,
        hoveredBpmnElement,
        selectedBpmnElement,
        destroy,
        isChanged: readonly(isChanged),
        getSubProcesses,
        getSubProcessById,
        selectSubProcess,
        getLanes,
        completeActiveActions,
        getRootElement,
        setRootElement,
        zoomToElementId,
        get elementRegistry() {
            return bpmnJs.value?.get("elementRegistry");
        },
        get canvas() {
            return bpmnJs.value?.get("canvas");
        },
        get selection() {
            return bpmnJs.value?.get("selection");
        },
        get eventBus() {
            return eventBus.value;
        },
        updateElementProperties,
        triggerDirectEditing,
    };
}
